import { useCallback } from "react";

import { useAuth0 } from "./auth0";
import { isPermissionRefusal } from "./permissions";

// One refresh answers every refusal that arrives while it is in flight: a page
// that fires several gated requests at once should not reload the profile once
// per request.
let refreshInFlight: Promise<void> | null = null;

/**
 * The profile — and with it the permissions and locations the dashboard draws
 * from — is fetched once at sign-in, so a permission withheld or a location
 * limit set mid-session leaves its controls on screen. When the API refuses an
 * action for either, reload the profile so those controls disappear without a
 * page reload. The
 * caller still surfaces the refusal's message; this only brings the screen up
 * to date.
 */
export function useRefreshOnPermissionRefusal(): (
  status: number,
  body: unknown
) => void {
  const { refreshUserProfile } = useAuth0();

  return useCallback(
    (status: number, body: unknown) => {
      if (!isPermissionRefusal(status, body) || refreshInFlight) return;

      refreshInFlight = refreshUserProfile().finally(() => {
        refreshInFlight = null;
      });
    },
    [refreshUserProfile]
  );
}
